import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang } from '@/i18n/LanguageContext';
import { reviewsData } from '@/data/reviewsData';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const GoogleReviews = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(1);

  // Responsive items per page
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setItemsPerPage(3);
      } else if (window.innerWidth >= 768) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(1);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalPages = Math.ceil(reviewsData.length / itemsPerPage);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalPages);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalPages) % totalPages);
  };

  const visibleReviews = reviewsData.slice(
    currentIndex * itemsPerPage,
    (currentIndex + 1) * itemsPerPage
  );

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-secondary/50 border border-brand-primary text-xs font-semibold text-brand-primary uppercase tracking-wider mb-5">
            {lang === 'en' ? 'Student Reviews' : 'Reseñas de Estudiantes'}
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-3">
            {lang === 'en' ? 'Trusted by Students. Proven by Results.' : 'Confiado por Estudiantes. Comprobado por Resultados.'}
          </h2>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-2xl font-extrabold text-[#EA4335]">5.0</span>
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-[#FBBC05] text-[#FBBC05]" />
              ))}
            </div>
            <span className="text-slate-500 font-medium text-sm ml-1">
              {lang === 'en' ? 'on Google' : 'en Google'}
            </span>
          </div>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto text-lg">
            {lang === 'en' 
              ? 'Hear from graduates who launched their dental careers with Royal Dental Assistant Academy.' 
              : 'Escucha a los graduados que lanzaron sus carreras dentales con Royal Dental Assistant Academy.'}
          </p>
        </div>

        <div className="relative">
          {/* Navigation Buttons */}
          <button 
            onClick={prevSlide}
            className="absolute -left-2 sm:-left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-xl border border-slate-100 flex items-center justify-center text-slate-400 hover:text-brand-primary hover:scale-110 transition-all z-10"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button 
            onClick={nextSlide}
            className="absolute -right-2 sm:-right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-xl border border-slate-100 flex items-center justify-center text-slate-400 hover:text-brand-primary hover:scale-110 transition-all z-10"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Reviews Grid */}
          <div className="overflow-hidden px-4 py-8">
            <AnimatePresence mode="wait">
              <motion.div 
                key={currentIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {visibleReviews.map((review) => (
                  <div key={review.id} className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-lg">
                          {review.author.charAt(0)}
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 text-sm sm:text-base leading-none mb-1">{review.author}</h4>
                          <span className="text-xs text-slate-500">{review.date}</span>
                        </div>
                      </div>
                      <svg viewBox="0 0 24 24" className="w-5 h-5 opacity-80">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                    </div>
                    
                    <div className="flex gap-1 mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                      ))}
                    </div>

                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed flex-grow">
                      "{review.text}"
                    </p>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
          
          {/* Pagination Dots */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    currentIndex === i ? 'bg-brand-primary w-6' : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              {lang === 'en' ? 'Ready to Become Our Next Success Story?' : '¿Listo para ser nuestra próxima historia de éxito?'}
            </h3>
            <button onClick={() => navigate('/application')} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-brand-primary border border-transparent rounded-full hover:bg-brand-primary/90 hover:scale-105 hover:shadow-lg hover:shadow-brand-primary/30">
              {lang === 'en' ? 'Apply Today or Schedule a Tour' : 'Aplica Hoy o Programa un Recorrido'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
